import { useCallback, useEffect, useRef, useState } from "react";
import type { ConversationMessage } from "../api/contract";

/**
 * While the admin panel is closed, a new reply from the admin shows as a small card above the
 * bubble, so the owner browsing their site still sees "your first draft is ready". Replies that
 * were already there when the page loaded, or that arrived with the panel open, never peek.
 *
 * With `onPreviewChanged`, a reply that changed the design while the panel is open calls it (the
 * workspace closes the chat on phones, so the owner sees the new preview) and peeks once it has.
 */
export function usePeek(messages: ConversationMessage[] | undefined, panelOpen: boolean, onPreviewChanged?: () => boolean) {
  const [peek, setPeek] = useState<ConversationMessage | null>(null);
  const seen = useRef<string | null | undefined>(undefined);

  const latest = messages ? [...messages].reverse().find((message) => message.role === "admin") ?? null : undefined;
  const latestRef = useRef(latest);
  latestRef.current = latest;
  const changedRef = useRef(onPreviewChanged);
  changedRef.current = onPreviewChanged;
  const latestId = latest === undefined ? undefined : latest?.id ?? null;

  useEffect(() => {
    if (latestId === undefined) return;
    const fresh = seen.current !== undefined && latestId !== null && latestId !== seen.current;
    if (seen.current === undefined || panelOpen) {
      seen.current = latestId;
      const message = latestRef.current;
      setPeek(fresh && message?.card?.kind === "design-updated" && changedRef.current?.() ? message : null);
      return;
    }
    if (fresh) {
      seen.current = latestId;
      setPeek(latestRef.current ?? null);
    }
  }, [latestId, panelOpen]);

  const dismiss = useCallback(() => setPeek(null), []);
  return { peek, dismiss };
}
